import { memo, useMemo, type FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { TreeViewBaseItem } from "@mui/x-tree-view";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import { useContactJobFunctionsQuery } from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import TreeSelect from "@/components/filters/TreeSelect/TreeSelect";
import { SELECTION_PRESETS } from "@/components/filters/TreeSelect/hooks/useHierarchicalSelection";

type Slice = { include: string[]; exclude: string[] };
const defaultSlice: Slice = { include: [], exclude: [] };

// Job function names and sub-function names are unique across the whole
// taxonomy (verified against the live dataset), so plain names double as
// stable tree-item ids — matches OppGen's DepthSelectInput contract, which
// also stores raw names (not synthetic ids) in the include/exclude arrays.
// Selection is independent like OppGen's flat multi-select: picking "Finance"
// sends just ["Finance"]; hermes matches job_functions values against both
// functions and sub-functions. (TreeSelect still auto-checks a parent once all
// its sub-functions are picked, which doesn't change the results.)
function buildTreeItems(
  functions: ReadonlyArray<
    | {
        id?: string | null;
        name?: string | null;
        subFunctions?: ReadonlyArray<string | null> | null;
      }
    | null
    | undefined
  >
): TreeViewBaseItem[] {
  return functions
    .filter(
      (f): f is { id: string; name: string; subFunctions: string[] } =>
        !!f && Boolean(f.name)
    )
    .map((f) => ({
      id: f.name,
      label: f.name,
      children: (f.subFunctions ?? [])
        .filter((s): s is string => Boolean(s))
        .map((s) => ({ id: s, label: s })),
    }));
}

const ContactJobFunctionFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("contactJobFunction");
  const { setSlice } = useCompanyFilterActions();

  const { data, loading } = useContactJobFunctionsQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });

  const treeItems = useMemo(
    () => buildTreeItems(data?.contactJobFunctions?.data ?? []),
    [data]
  );

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Include the following job functions
        </Typography>
        <TreeSelect
          id="contacts-job-function-include"
          items={treeItems}
          selectedItems={slice.include}
          onSelectItems={(include) =>
            setSlice("contactJobFunction", { ...slice, include })
          }
          loading={loading}
          placeholder="Search job functions..."
          checkboxTestId="contacts-job-function-include-checkbox"
          selectionRules={SELECTION_PRESETS.independent}
        />
      </Box>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Exclude the following job functions
        </Typography>
        <TreeSelect
          id="contacts-job-function-exclude"
          items={treeItems}
          selectedItems={slice.exclude}
          onSelectItems={(exclude) =>
            setSlice("contactJobFunction", { ...slice, exclude })
          }
          loading={loading}
          placeholder="Search job functions..."
          checkboxTestId="contacts-job-function-exclude-checkbox"
          selectionRules={SELECTION_PRESETS.independent}
        />
      </Box>
    </Stack>
  );
});

ContactJobFunctionFilter.displayName = "ContactJobFunctionFilter";

export const contactJobFunctionModule: FilterModule<Slice> = {
  key: "contactJobFunction",
  defaultSlice,
  Component: ContactJobFunctionFilter,
  toCompanyFilters: (slice): Partial<CompanyFilters> =>
    slice.include.length > 0 || slice.exclude.length > 0
      ? {
          contactFilters: {
            jobFunctions: { include: slice.include, exclude: slice.exclude },
          },
        }
      : {},
  fromCompanyFilters: (filters): Slice => ({
    include: filters.contactFilters?.jobFunctions?.include ?? [],
    exclude: filters.contactFilters?.jobFunctions?.exclude ?? [],
  }),
  getChips: (slice, setSlice) => [
    ...slice.include.map((f) => ({
      id: `contacts-function-include-${f}`,
      label: `Function: ${f}`,
      filterKey: "contactJobFunction" as const,
      onRemove: () =>
        setSlice({ ...slice, include: slice.include.filter((x) => x !== f) }),
    })),
    ...slice.exclude.map((f) => ({
      id: `contacts-function-exclude-${f}`,
      label: `Excl. Function: ${f}`,
      filterKey: "contactJobFunction" as const,
      onRemove: () =>
        setSlice({ ...slice, exclude: slice.exclude.filter((x) => x !== f) }),
    })),
  ],
  getCount: (slice) => slice.include.length + slice.exclude.length,
};
